import React from 'react';
import { Paragraph } from 'src/shared/design-system/Text';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled, { css } from 'styled-components';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';

interface IConsumerOrderDishSelectionRowProps {
  title: React.ReactNode;
  button?: React.ReactElement | null;
  blur: boolean;
  titleBadge?: React.ReactElement;
  subTitle?: string;
  suppressTitle?: boolean;
}

const FoodItemsRowContainerHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-family: ${Font.Family.SANS};
  margin-left: -15px;
  margin-right: -15px;
  padding-left: 15px;
  padding-right: 15px;
`;

const FoodItemsRowContainer = styled.div<{ blur: boolean }>`
  margin-top: 30px;
  margin-bottom: 30px;

  ${FoodItemsRowContainerHeader} {
    margin-bottom: 30px;
  }

  ${ifProp(
    'blur',
    css`
      filter: blur(4px);
      pointer-events: none;
    `
  )} @media(max-width: ${ScreenWidth.SMALL}) {
    margin-top: 12px;
    margin-bottom: 12px;

    ${FoodItemsRowContainerHeader} {
      padding-top: 12px;
      padding-bottom: 12px;
      margin-bottom: 0px;
    }

    :first-child {
      margin-top: 0;

      ${FoodItemsRowContainerHeader} {
        padding-top: 8px;
        padding-bottom: 8px;
        margin-bottom: 0;
      }
    }

    :last-child {
      margin-bottom: 0;
    }
  }
`;

const TitleText = styled(Paragraph)`
  display: flex;
  align-items: center;
  gap: ${Spacing.SINGLE};
  font-weight: bold;
`;

const FoodItemsRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-left: -15px;
  margin-right: -15px;
`;

const Subtitle = styled.p`
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.MEDIUM};
  font-size: ${Font.Size.S};
  color: ${Colors.GRAY_DARK_20};
  line-height: 120%;
  margin: ${Spacing.HALF} 0 0 0;
  width: 100%;

  ${minScreen(ScreenWidth.SMALL)} {
    gap: ${Font.Size.XXL};
    margin: ${Spacing.QUARTER} 0 0 0;
  }
`;

export const ConsumerOrderDishSelectionRow: React.FC<React.PropsWithChildren<IConsumerOrderDishSelectionRowProps>> = ({
  children,
  title,
  button,
  blur,
  titleBadge,
  subTitle,
  suppressTitle,
}) => {
  const items = React.Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return (
    <FoodItemsRowContainer blur={blur}>
      {!suppressTitle && (
        <>
          <FoodItemsRowContainerHeader>
            <div className={'flex w-fit'}>
              <TitleText size='20px' mobileSize='18px' bold margin='0'>
                {title}
              </TitleText>
              {titleBadge && <>{titleBadge}</>}
            </div>
            {button}
          </FoodItemsRowContainerHeader>
          {subTitle && <Subtitle>{subTitle}</Subtitle>}
        </>
      )}
      <FoodItemsRowWrapper>{items}</FoodItemsRowWrapper>
    </FoodItemsRowContainer>
  );
};
